import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { Box, Button, FormField, Pagination, Select, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import ServiceCollection from 'src/services/ServiceCollection';
import CONSTANTS from 'src/utils/constants';
import StringUtils from 'src/utils/stringUtils';
import { getFeatureFlags } from 'src/services/FeatureFlagsService';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import SegmentFeatureShell from 'src/components/segment-cdt/SegmentFeatureShell';
import SegmentPageHeader from 'src/components/segment-cdt/SegmentPageHeader';
import SegmentGrid from 'src/components/segment-cdt/SegmentGrid';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import { buildColumnDefinitions, flattenRow } from 'src/components/segment-cdt/detail/segmentDetailColumns';
import { formatSegmentType } from 'src/models/segment-cdt/SegmentTableListRow';
import { SearchSegmentDataTableRowsRequest } from 'src/models/segment-cdt/SegmentCdtTypes';
import { DbConjunctor, DbMainExpression, DbOperator, DbSortOrder } from 'src/models/common/TPESearchRequest';
import ReactTableFilter from 'src/models/common/ReactTableFilter';

// Wait this long after the last filter keystroke before searching, matching the CLI records view.
const FILTER_DEBOUNCE_MS = 1500;

/** yyyyMM -> "MMM YYYY" via moment (the repo's date formatter); passes a non-yyyyMM value through. */
function formatPeriod(period?: string): string {
    if (!period || !/^\d{6}$/.test(period)) {
        return period ?? '';
    }
    return moment(period, 'YYYYMM').format('MMM YYYY');
}

/** MONTHLY -> "Monthly"; blank/undefined stays blank. */
function titleCase(s?: string): string {
    return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';
}

/** Build a server filterExpression (AND of per-column CONTAINS) from the react-table filter values. */
function toFilterExpression(filters: ReactTableFilter[]): DbMainExpression | undefined {
    const active = (filters || []).filter((f) => !StringUtils.isNullOrEmpty(f.value));
    if (!active.length) {
        return undefined;
    }
    return {
        conjunctor: DbConjunctor.AND,
        expressions: active.map((f) => ({ attributeName: f.id, operator: DbOperator.CONTAINS, values: [f.value] })),
    };
}

/** Actions deferred to later CRs — rendered as stubs so the surface is complete. No-op this CR. */
const STUB_ACTIONS: { label: string; iconName?: string }[] = [
    { label: 'Pull balance' }, // future CR: trigger a full-table GL pull (triggerCDTGLPull)
    { label: 'Upload', iconName: 'upload' }, // future CR: upload rows from a template (uploadSegmentDataTable)
    { label: 'Download', iconName: 'download' }, // future CR: download the table (downloadSegmentDataTable)
    { label: 'Validate' }, // future CR: run validations (getSegmentValidations)
    { label: 'Add columns', iconName: 'add-plus' }, // future CR: add columns to an existing table
];

/**
 * Body of the segment detail data-viewer. Reads `{ state, dispatch, services }` from the shell context,
 * fetches the definition + periods on mount, and searches one period's rows (numbered paging). Renders
 * the header + stub toolbar + period selector + dynamic grid + pager.
 */
function SegmentTableDetailContent(props: { tableId: string }) {
    const { tableId } = props;
    const { state, dispatch, services } = useContext(SegmentTablesContext);
    const detail = state.detail;
    const svc = services.segmentDataTablesService;
    // "Filter rows" toggle — reveals the per-column filter inputs (parity with the CLI records view).
    const [showFilters, setShowFilters] = useState(false);
    // Bumped to remount the grid so react-table's internal filter state is discarded on toggle-off.
    const [gridKey, setGridKey] = useState(0);
    const filterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Set when we deliberately remount the grid; lets onSortBy ignore the one empty event the fresh
    // grid fires on mount so it doesn't wipe an active sort.
    const skipEmptySortRef = useRef(false);

    const [defResult, defLoading, defError] = svc.getSegmentDataTable(tableId);
    const [periodsResult, periodsLoading, periodsError] = svc.getSegmentPeriods(tableId);

    // Only search once a period is selected. `page` is 1-based; omitting sort lets the backend default it.
    // Active filters become a server-side filterExpression (AND of per-column CONTAINS).
    const searchRequest: SearchSegmentDataTableRowsRequest | undefined = useMemo(() => {
        if (!detail.selectedPeriod) {
            return undefined;
        }
        const filterExpression = toFilterExpression(detail.filters);
        return {
            tableId,
            period: detail.selectedPeriod,
            page: detail.page,
            pageSize: detail.pageSize,
            ...(detail.sortField ? { sortField: detail.sortField, sortOrder: detail.sortOrder } : {}),
            ...(filterExpression ? { filterExpression } : {}),
        };
    }, [tableId, detail.selectedPeriod, detail.page, detail.pageSize, detail.sortField, detail.sortOrder, detail.filters]);
    const [rowsResult, rowsLoading, rowsError] = svc.searchSegmentDataTableRows(searchRequest);

    useEffect(() => {
        if (defResult?.table) {
            dispatch(ACTIONS.SET_DETAIL_DEFINITION.withPayload(defResult.table));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defResult]);

    useEffect(() => {
        if (periodsResult) {
            dispatch(ACTIONS.SET_DETAIL_PERIODS.withPayload(periodsResult.periods ?? []));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodsResult]);

    useEffect(() => {
        if (rowsResult) {
            dispatch(ACTIONS.SET_DETAIL_SEARCH_RESULT.withPayload(rowsResult));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowsResult]);

    useEffect(() => {
        const err = defError || periodsError || rowsError;
        if (!StringUtils.isNullOrEmpty(err)) {
            services.messageService.showErrorBanner(err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defError, periodsError, rowsError]);

    const columns = detail.definition?.columns ?? [];
    const columnDefinitions = useMemo(() => buildColumnDefinitions(columns), [detail.definition]); // eslint-disable-line react-hooks/exhaustive-deps
    const data = useMemo(
        () => (detail.searchResult?.rows ?? []).map((r) => flattenRow(r, columns)),
        [detail.searchResult, detail.definition], // eslint-disable-line react-hooks/exhaustive-deps
    );

    const periodOptions = useMemo(
        () => detail.periods.map((p) => ({ label: formatPeriod(p.period), value: p.period ?? '' })),
        [detail.periods],
    );
    const selectedPeriodOption = detail.selectedPeriod
        ? { label: formatPeriod(detail.selectedPeriod), value: detail.selectedPeriod }
        : null;

    const onSortBy = (sortBy: any[]) => {
        const s = sortBy && sortBy[0];
        const sortField = s ? s.id : undefined;
        // Backend/DbSortOrder convention is ASCENDING/DESCENDING (what the CLI search sends), not ASC/DESC.
        const sortOrder = s ? (s.desc ? DbSortOrder.DESCENDING : DbSortOrder.ASCENDING) : undefined;
        // A deliberate remount (filter toggle-off) makes the fresh grid fire one empty onSortBy;
        // ignore it so an active sort isn't cleared. TPEReactTable can't be seeded with sortBy, so
        // the sort persists in state (and the re-search) even though the caret resets on that remount.
        if (!sortField && skipEmptySortRef.current) {
            skipEmptySortRef.current = false;
            return;
        }
        // Skip the empty no-op react-table also fires on first mount and any unchanged re-toggle.
        if (sortField === detail.sortField && sortOrder === detail.sortOrder) {
            return;
        }
        dispatch(ACTIONS.SET_DETAIL_SORT.withPayload({ sortField, sortOrder }));
    };

    // Debounce per-column filtering so a search fires once the user pauses, not per keystroke (CLI
    // parity). Skips the empty no-op react-table fires on mount.
    const onFilterChange = (filters: ReactTableFilter[]) => {
        if ((filters?.length ?? 0) === 0 && detail.filters.length === 0) {
            return;
        }
        if (filterTimer.current) {
            clearTimeout(filterTimer.current);
        }
        filterTimer.current = setTimeout(
            () => dispatch(ACTIONS.SET_DETAIL_FILTERS.withPayload(filters ?? [])),
            FILTER_DEBOUNCE_MS,
        );
    };
    useEffect(() => () => { if (filterTimer.current) clearTimeout(filterTimer.current); }, []);

    const def = detail.definition;
    const toggleFilters = () => {
        const next = !showFilters;
        setShowFilters(next);
        if (!next) {
            // Clear applied filters AND remount the grid (bump key) so react-table's own filter-input
            // state is discarded; otherwise re-opening shows stale text over unfiltered data
            // (autoResetFilters is off via preserveStateOnDataChange). Mirrors the CLI tableKey remount.
            if (filterTimer.current) {
                clearTimeout(filterTimer.current);
            }
            skipEmptySortRef.current = true; // the remount fires one empty onSortBy — don't let it clear the sort
            dispatch(ACTIONS.SET_DETAIL_FILTERS.withPayload([]));
            setGridKey((k) => k + 1);
        }
    };

    return (
        <>
            <SegmentPageHeader
                title={def?.tableName ?? CONSTANTS.PAGE_NAV.SEGMENT_DATA_TABLE_DETAILS.TITLE}
                actions={
                    <SpaceBetween direction="horizontal" size="xs">
                        {STUB_ACTIONS.map((a) => (
                            // Stub: real behaviour lands in a later CR. Intentionally a no-op this CR.
                            <Button key={a.label} iconName={a.iconName as any} onClick={() => undefined}>
                                {a.label}
                            </Button>
                        ))}
                    </SpaceBetween>
                }
            />
            {/* Body inset to match the grey header's 1.2rem padding so the name, metadata, period,
                and grid all share one left edge; SpaceBetween gives one consistent vertical rhythm. */}
            <div style={{ padding: '1.2rem' }}>
                <SpaceBetween size="l">
                    {def && (
                        <SpaceBetween direction="horizontal" size="xl">
                            <span><strong>Segment Type:</strong> {formatSegmentType(def.segmentType)}</span>
                            <span><strong>Segment Population (Custom CoA):</strong> {def.segmentCustomCoaId || 'None'}</span>
                            <span><strong>Cadence:</strong> {titleCase(def.refreshCadence) || 'None'}</span>
                            <span><strong>Owner:</strong> {def.owner || '—'}</span>
                        </SpaceBetween>
                    )}
                    {/* Toolbar row: period on the left; filter toggle + pagination on the right (top,
                        matching the CLI records view — not a bottom pager). */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '12px' }}>
                        <div style={{ maxWidth: '300px', flex: '0 0 300px' }}>
                            <FormField label="Period">
                                <Select
                                    selectedOption={selectedPeriodOption}
                                    options={periodOptions}
                                    statusType={periodsLoading ? 'loading' : 'finished'}
                                    loadingText="Loading periods"
                                    placeholder="Select a period"
                                    empty="No periods with data yet"
                                    onChange={({ detail: d }) => dispatch(ACTIONS.SELECT_DETAIL_PERIOD.withPayload(d.selectedOption.value))}
                                />
                            </FormField>
                        </div>
                        <SpaceBetween direction="horizontal" size="s" alignItems="center">
                            <Button
                                iconName="filter"
                                variant={showFilters ? 'primary' : 'normal'}
                                onClick={toggleFilters}
                            >
                                Filter rows
                            </Button>
                            <Pagination
                                // Use the server-echoed page (after any cap/default), falling back to the requested page.
                                currentPageIndex={detail.searchResult?.page ?? detail.page}
                                pagesCount={detail.searchResult?.pagesCount ?? 1}
                                onChange={({ detail: d }) => dispatch(ACTIONS.SET_DETAIL_PAGE.withPayload(d.currentPageIndex))}
                            />
                        </SpaceBetween>
                    </div>
                    <div className="tableContainer">
                        <SegmentGrid
                            // nonGridTable = the shared display:block table layout (as the CLI records grid /
                            // COA use) so a dynamic, variable column set sizes to content instead of the fixed
                            // grid-template-columns the list grids rely on. `key` remounts on filter toggle-off.
                            key={gridKey}
                            className="nonGridTable segmentDataTableRowsGrid"
                            loading={defLoading || rowsLoading}
                            data={data}
                            columnDefinitions={columnDefinitions}
                            sortable={true}
                            onSortBy={onSortBy}
                            showFilters={showFilters}
                            onFilter={onFilterChange}
                            preserveStateOnDataChange={true}
                            emptyMessage={detail.selectedPeriod ? 'No rows in this period.' : 'Select a period to view rows.'}
                        />
                    </div>
                </SpaceBetween>
            </div>
        </>
    );
}

/**
 * Route entry for viewing a Segment Data Table's rows. Decodes the base64 `tableId` from the URL and
 * hosts the body in the shared `SegmentFeatureShell` (which supplies `SegmentTablesContext`, the
 * breadcrumb, and the page container). Reached from the list tab's Name-link.
 */
export default function SegmentTableDetailView(props: { services: ServiceCollection }) {
    const { encodedTableId } = useParams<{ encodedTableId: string }>();
    // Guard: a malformed (non-base64) id in the URL must not throw and white-screen the route.
    let tableId = '';
    try {
        tableId = atob(encodedTableId);
    } catch {
        tableId = '';
    }
    const { SEGMENT_DATA_TABLE_DETAILS } = CONSTANTS.PAGE_NAV;

    // Ship dark: gate the whole detail route on segmentCdtEnabled (AppConfig), like the list tab, so a
    // direct/bookmarked URL can't reach it while the feature is flagged off — no deploy needed to hide it.
    const [flagEnabled, setFlagEnabled] = useState<boolean | null>(null);
    useEffect(() => {
        let ignore = false;
        getFeatureFlags()
            .then((flags) => { if (!ignore) setFlagEnabled(!!flags.segmentCdtEnabled); })
            .catch(() => { if (!ignore) setFlagEnabled(false); });
        return () => { ignore = true; };
    }, []);

    if (flagEnabled === null) {
        return null; // flag still resolving
    }
    if (!flagEnabled) {
        return <Box textAlign="center" padding="l">This page is not available.</Box>;
    }

    return (
        <SegmentFeatureShell
            services={props.services}
            breadcrumb={{
                label: SEGMENT_DATA_TABLE_DETAILS.TITLE,
                title: SEGMENT_DATA_TABLE_DETAILS.TITLE,
                description: SEGMENT_DATA_TABLE_DETAILS.DESCRIPTION,
            }}
        >
            <SegmentTableDetailContent tableId={tableId} />
        </SegmentFeatureShell>
    );
}
